import { AspectRatio, Box, Grid, Group, Image, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import type { Icon } from "@tabler/icons-react";
import { Reveal } from "$/components/reveal";
import { Section } from "$/components/section";
import { differentials } from "$/data/differentials";

const accentMap: Record<string, { bg: string; fg: string; border: string }> = {
  green: {
    bg: "var(--mantine-color-green-0)",
    fg: "var(--mantine-color-green-7)",
    border: "var(--mantine-color-green-2)",
  },
  orange: {
    bg: "var(--mantine-color-orange-0)",
    fg: "var(--mantine-color-orange-7)",
    border: "var(--mantine-color-orange-2)",
  },
  blue: {
    bg: "var(--mantine-color-blue-0)",
    fg: "var(--mantine-color-blue-7)",
    border: "var(--mantine-color-blue-2)",
  },
  violet: {
    bg: "var(--mantine-color-violet-0)",
    fg: "var(--mantine-color-violet-7)",
    border: "var(--mantine-color-violet-2)",
  },
  teal: {
    bg: "var(--mantine-color-teal-0)",
    fg: "var(--mantine-color-teal-7)",
    border: "var(--mantine-color-teal-2)",
  },
};

export function Differentials() {
  return (
    <Section bg="gray.0" id="differentials">
      <Grid gap={{ base: "xl", md: 64 }} align="center">
        <Grid.Col span={{ base: 12, md: 6 }} className="differentials-media">
          <Reveal>
            <AspectRatio
              ratio={16 / 9}
              className="differentials-image"
              style={{
                borderRadius: 24,
                border: "1px solid var(--mantine-color-gray-3)",
                boxShadow: "0 30px 60px -30px rgba(15, 23, 42, 0.25)",
                overflow: "hidden",
              }}
            >
              <Image src="/differentials.png" alt="Loja usando o Verdana no balcão" fit="cover" />
            </AspectRatio>
          </Reveal>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="xl">
            <Stack gap="md">
              <Text fz="xs" fw={700} c="green.7" style={{ letterSpacing: "0.15em" }}>
                POR QUE ESCOLHER
              </Text>

              <Title
                order={2}
                fz={{ base: 34, md: 48 }}
                fw={800}
                lh={1.05}
                style={{ letterSpacing: "-0.035em" }}
              >
                Feito pra rodar no{" "}
                <Text
                  component="span"
                  inherit
                  c="green.7"
                  style={{
                    textDecoration: "underline",
                    textDecorationColor: "var(--mantine-color-green-4)",
                    textUnderlineOffset: 6,
                    textDecorationThickness: 4,
                  }}
                >
                  ritmo
                </Text>{" "}
                da sua loja.
              </Title>
            </Stack>

            <Stack gap="lg">
              {differentials.map((d, idx) => (
                <Reveal key={d.title} delay={idx * 90}>
                  <DifferentialRow
                    icon={d.icon}
                    title={d.title}
                    description={d.description}
                    accent={d.accent}
                  />
                </Reveal>
              ))}
            </Stack>
          </Stack>
        </Grid.Col>
      </Grid>
    </Section>
  );
}

type RowProps = {
  icon: Icon;
  title: string;
  description: string;
  accent: string;
};

function DifferentialRow({ icon: Icon, title, description, accent }: RowProps) {
  const colors = accentMap[accent] ?? accentMap.green;

  return (
    <Group align="flex-start" gap="md" wrap="nowrap">
      <ThemeIcon
        size={44}
        radius="xl"
        style={{
          background: colors.bg,
          color: colors.fg,
          border: `1px solid ${colors.border}`,
          flexShrink: 0,
        }}
      >
        <Icon size={20} />
      </ThemeIcon>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text fw={700} fz={{ base: 17, md: 19 }} c="dark.9" lh={1.25}>
          {title}
        </Text>
        <Text c="gray.7" fz={{ base: 14, md: 15 }} lh={1.55} mt={4}>
          {description}
        </Text>
      </Box>
    </Group>
  );
}
